import get from "lodash/get";
import {useCallback, useEffect, useState} from "react";
import {useFormContext} from "react-hook-form";

import Button from "@/components/button/Button.tsx";
import {FormLabel} from "@/components/form/FormLabel.tsx";
import {FormSubtext} from "@/components/formSubtext/FormSubtext.tsx";
import {FieldProps} from "@/pages/checks/components/form/fields/common.ts";

interface RDAPLookupFieldProps extends FieldProps {}

const FIELDS = ["registrar_id", "registrar_name", "nameservers"];

export const RDAPLookupField = ({
  name,
  tooltip,
  label,
  className,
  helpText,
  supportLink,
  required,
}: RDAPLookupFieldProps) => {
  const {
    setValue,
    watch,
    clearErrors,
    formState: {errors},
    setError,
  } = useFormContext();
  const value = watch(name) || "";
  const [active, setActive] = useState(true);
  const fieldError = errors[name]?.message;

  const listenToTestRun = useCallback(
    (event: CustomEvent<object>) => {
      const result: string = get(event.detail, "result") || "";
      if (!result) {
        setError(name, {message: "field is required to be populated by a test run"});
        return;
      }

      const items = result.split(/\n/);
      // skip check summary
      items.shift();
      const rdap = new Map(
        items
          .map((line) => line.trim())
          .filter((line) => !!line && FIELDS.includes(line.split("=")[0].trim()))
          .map((item) => {
            const [key, value] = item.split("=", 2);
            return [key, value];
          })
      );

      let value = "";
      if (rdap.has("nameservers")) {
        value = `nameservers:${rdap.get("nameservers")}`;
      }
      if (rdap.has("registrar_id")) {
        value = `registrar_id:${rdap.get("registrar_id")}\n${value}`;
      }
      if (rdap.has("registrar_name")) {
        value = `registrar_name:${rdap.get("registrar_name")}\n${value}`;
      }
      clearErrors([name]);
      setValue(name, value, {shouldDirty: true});
      setActive(true);
    },
    [clearErrors, setValue, name, setError, setActive]
  );

  useEffect(() => {
    // listen on custom event until test run is migrated to GQL so we can trigger it from here
    // TestRunner has too many edge cases to extra specific usage here
    window.addEventListener("testRun", listenToTestRun);
    return () => {
      window.removeEventListener("testRun", listenToTestRun);
    };
  }, [listenToTestRun]);

  return (
    <>
      <div className={className}>
        <FormLabel helpText={tooltip} forId={name} required={required}>
          {label}
        </FormLabel>
        <textarea
          id={name}
          className="form-control"
          name={name}
          value={value}
          required={required}
          disabled={true}
          rows={5}
        />
        <span>{fieldError && <small className="invalid-feedback d-block">{fieldError.toString()}</small>}</span>
        <FormSubtext subText={helpText} supportLink={supportLink} />
      </div>
      <div className="col-12">
        <Button
          className="mt-2"
          onClick={() => {
            if (active) {
              // reset active state after timeout in case there is a bug in test runner
              setTimeout(() => {
                if (!active) {
                  setActive(true);
                }
              }, 1000 * 30);
            }
            setActive(false);
            document.querySelector<HTMLButtonElement>('[data-wizard="devices-testrunner-run-test"]')?.click();
          }}
          disabled={!active}
        >
          Refresh
        </Button>
      </div>
    </>
  );
};
